import {useContext, useEffect, useMemo, useState} from 'react';

import {Autocomplete, createFilterOptions, TextField} from '@mui/material';

import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {StandardSkillsService} from '../../../profile/services/StandardSkillsService';
import type {StandardSkill} from '../../../profile/types';
import type {SkillCategory} from '../../types/DepartmentTargetProfileTypes';

type SkillOption = StandardSkill & {inputValue?: string};

const filter = createFilterOptions<SkillOption>();

export interface Props {
  skillCategory: SkillCategory;
  occupationResourceId?: string;
  excludeStandardSkillResourceIds?: ReadonlySet<string>;
  excludeSkillNames?: ReadonlySet<string>;
  disabled?: boolean;
  label: string;
  createLabel: (name: string) => string;
  onSelectExisting: (skill: StandardSkill) => void;
  onSelectCustom: (name: string) => void;
}

export const TargetProfileSkillAutocomplete = ({
  skillCategory,
  occupationResourceId,
  excludeStandardSkillResourceIds,
  excludeSkillNames,
  disabled,
  label,
  createLabel,
  onSelectExisting,
  onSelectCustom,
}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const [options, setOptions] = useState<SkillOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState<SkillOption | null>(null);
  const service = useMemo(() => new StandardSkillsService(), []);
  const excludeNamesKey = useMemo(
    () => (excludeSkillNames ? [...excludeSkillNames].sort().join('|') : ''),
    [excludeSkillNames]
  );

  useEffect(() => {
    let cancelled = false;
    service
      .getStandardSkills(currentLanguage, {
        skillCategory,
        occupationResourceId,
      })
      .then((skills) => {
        if (!cancelled) {
          setOptions(skills);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentLanguage, excludeNamesKey, occupationResourceId, service, skillCategory]);

  const filteredBaseOptions = useMemo(() => {
    return options.filter((skill) => {
      if (excludeStandardSkillResourceIds?.has(skill.resourceId)) {
        return false;
      }
      const name = skill.name[currentLanguage]?.toLowerCase();
      if (name && excludeSkillNames?.has(name)) {
        return false;
      }
      return true;
    });
  }, [options, excludeStandardSkillResourceIds, excludeSkillNames, currentLanguage]);

  return (
    <Autocomplete
      size='small'
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      disabled={disabled}
      options={filteredBaseOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        if (option.inputValue) {
          return option.inputValue;
        }
        return option.name[currentLanguage] || option.name.en || '';
      }}
      isOptionEqualToValue={(a, b) => {
        if (typeof a === 'string' || typeof b === 'string') {
          return false;
        }
        if (a.inputValue || b.inputValue) {
          return a.inputValue === b.inputValue;
        }
        return a.resourceId === b.resourceId;
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const trimmed = params.inputValue.trim();
        if (!trimmed) {
          return filtered;
        }

        const alreadyListed = opts.some(
          (option) => (option.name[currentLanguage] || option.name.en || '').toLowerCase() === trimmed.toLowerCase()
        );
        const alreadyOnProfile = excludeSkillNames?.has(trimmed.toLowerCase());
        if (!alreadyListed && !alreadyOnProfile) {
          filtered.push({
            resourceId: `new:${trimmed}`,
            name: {en: trimmed, de: trimmed},
            inputValue: trimmed,
            skillCategory,
          });
        }
        return filtered;
      }}
      renderOption={(props, option) => (
        <li {...props}>
          {option.inputValue
            ? createLabel(option.inputValue)
            : option.name[currentLanguage] || option.name.en}
        </li>
      )}
      onChange={(_, selected) => {
        if (!selected) {
          return;
        }

        if (typeof selected === 'string') {
          const name = selected.trim();
          if (name) {
            onSelectCustom(name);
          }
        } else if (selected.inputValue) {
          onSelectCustom(selected.inputValue.trim());
        } else {
          onSelectExisting(selected);
        }

        setValue(null);
        setInputValue('');
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{mt: 1, mb: 0.5, maxWidth: 420}}
    />
  );
};
